import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getLocaleFromPathname, locPath } from '../i18n/paths';
import { gsap } from 'gsap';
import { usePrefersReducedMotion } from '../hooks/usePrefersReducedMotion';

const TransitionContext = createContext({
  start: async (_path) => {},
  startWithImage: async (_path, _imageData) => {},
  imageTransitionData: null,
});

export const usePageTransition = () => useContext(TransitionContext);

const resetScroll = (lock) => {
  const lenis = typeof window !== 'undefined' ? window.__lenis : null;
  if (lenis?.scrollTo) {
    lenis.scrollTo(0, { immediate: true, lock });
    return;
  }
  window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
};

// The full-screen wipe between pages, built imperatively because it has to
// outlive the unmounting route. `aria-hidden` keeps the decorative percentage
// counter out of the accessibility tree.
const createOverlay = () => {
  const overlay = document.createElement('div');
  overlay.className = 'preloader';
  overlay.setAttribute('aria-hidden', 'true');
  Object.assign(overlay.style, {
    position: 'fixed',
    top: '0',
    left: '0',
    width: '100vw',
    height: '100vh',
    backgroundColor: '#262624',
    zIndex: '9999',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  });

  const loader = document.createElement('div');
  loader.className = 'loader';

  const progress = document.createElement('div');
  progress.className = 'loader-progress';
  Object.assign(progress.style, {
    position: 'absolute',
    bottom: '24px',
    left: '50%',
    transform: 'translateX(-50%)',
    color: '#f2f3d9',
    fontFamily: 'Space Grotesk, sans-serif',
    fontWeight: '700',
    fontSize: '18px',
    letterSpacing: '-0.4px',
  });
  progress.textContent = '0%';

  overlay.appendChild(loader);
  overlay.appendChild(progress);
  document.body.appendChild(overlay);

  return { overlay, loader, progress };
};

export const TransitionProvider = ({ children }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const lang = getLocaleFromPathname(pathname);
  const [imageTransitionData, setImageTransitionData] = useState(null);
  const reducedMotion = usePrefersReducedMotion();

  const resolvePath = useCallback(
    (path) => (typeof path === 'string' ? locPath(lang, path) : path),
    [lang]
  );

  // Reset imageTransitionData after it's been used (after transition completes)
  useEffect(() => {
    if (imageTransitionData) {
      const timer = setTimeout(() => {
        setImageTransitionData(null);
      }, 2000); // Reset after 2 seconds (enough time for page to use it)
      return () => clearTimeout(timer);
    }
  }, [imageTransitionData]);

  const run = useCallback(
    (path, textElements) =>
      new Promise((resolve) => {
        // Motion-sensitive visitors get a plain navigation: no wipe, no
        // rotating loader, no scroll hijack.
        if (reducedMotion) {
          resetScroll(false);
          navigate(resolvePath(path));
          resolve();
          return;
        }

        if (textElements?.length) {
          gsap.to(textElements, { opacity: 0, duration: 0.3, ease: 'power2.out' });
        }

        const { overlay, loader, progress } = createOverlay();

        const counter = { value: 0 };
        gsap.to(counter, {
          value: 100,
          duration: 1.2,
          ease: 'linear',
          onUpdate: () => {
            progress.textContent = `${Math.min(100, Math.round(counter.value))}%`;
          },
        });

        gsap.to(loader, {
          rotation: '+=180',
          duration: 1.2,
          ease: 'power4.inOut',
          onComplete: () => {
            resetScroll(true);
            navigate(resolvePath(path));

            gsap.to(loader, { scale: 0, duration: 0.8, ease: 'power4.inOut' });
            gsap.to(overlay, {
              clipPath: 'polygon(0% 0%, 100% 0%, 100% 0%, 0% 0%)',
              duration: 1,
              ease: 'power4.inOut',
              onComplete: () => {
                overlay.remove();
                resetScroll(false);
                resolve();
              },
            });
          },
        });
      }),
    [navigate, resolvePath, reducedMotion]
  );

  const start = useCallback((path) => run(path), [run]);

  const startWithImage = useCallback(
    (path, imageData) => {
      if (!imageData) return run(path);
      // Stored for the destination page, which animates the shared image in.
      setImageTransitionData(imageData);
      return run(path, imageData.textElements);
    },
    [run]
  );

  const value = useMemo(
    () => ({ start, startWithImage, imageTransitionData }),
    [start, startWithImage, imageTransitionData]
  );

  return <TransitionContext.Provider value={value}>{children}</TransitionContext.Provider>;
};

export default TransitionProvider;
